import { extractGraphQLErrorCodes, type ClientError } from '~/utils';
import { emptyMapping, isOrdinaryHeader } from './safeMapping';
import { SELLER_FIELDS, TARGET_FIELD_KEYS } from './targetFields';
import type { ColumnMapping, ParsedFile } from './types';

const TOO_MANY_PROPOSALS = 'too-many-proposals';

/** The key of the notice for a proposal that failed, or undefined when none did. */
export function proposalFailureKeyOf(error: unknown): string | undefined {
  if (error == null) {
    return undefined;
  }
  const refused = extractGraphQLErrorCodes(error as ClientError).includes(
    TOO_MANY_PROPOSALS
  );
  return refused ? 'mapping.proposal_refused' : 'mapping.proposal_failed';
}

const MAX_SAMPLES = 3;
const MAX_SAMPLE_LENGTH = 60;

export function proposalRequestOf(file: ParsedFile) {
  const columns: { header: string; samples: string[] }[] = [];
  for (const header of file.headers) {
    if (!isOrdinaryHeader(header)) continue;
    const samples: string[] = [];
    const seen = new Set<string>();
    for (const row of file.rows) {
      const value = (row[header] ?? '').trim();
      if (value === '' || seen.has(value)) continue;
      seen.add(value);
      samples.push(value.slice(0, MAX_SAMPLE_LENGTH));
      if (samples.length >= MAX_SAMPLES) break;
    }
    columns.push({ header, samples });
  }

  return {
    columns,
    fields: SELLER_FIELDS.map(({ key, label }) => ({ key, label })),
  };
}

/** The model's answer is untrusted JSON: only platform field keys under ordinary headers count. */
export function proposalMappingOf(data: unknown): ColumnMapping {
  const mapping = emptyMapping();
  if (data && typeof data === 'object') {
    for (const [header, field] of Object.entries(data)) {
      if (
        typeof field === 'string' &&
        TARGET_FIELD_KEYS.has(field) &&
        isOrdinaryHeader(header)
      ) {
        mapping[header] = field;
      }
    }
  }
  return mapping;
}
